import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import UserRole from '@careerstart/wae-common/src/main/constants/user-role';
import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';

import LocationIcon from '../../assets/icons/LocationIcon';
import StatusIcon from '../../assets/icons/StatusIconInFilters.svg';
import FreeTextSearchFilter, { SEARCHBAR_BACKGROUND } from '../../components/FreeTextSearchFilter';
import MultipleSelectDropdownChip from '../../components/MultipleSelectDropdownChip/MultipleSelectDropdownChip';
import NoResultsCard from '../../components/NoResultsCard/NoResultsCard';
import { SearchableSelectDropdownFilterDeprecated } from '../../components/SearchableSelectDropdownFilter/SearchableSelectDropdownFilter';
import { SelectDropdownFilter } from '../../components/SelectDropdownFilter/SelectDropdownFilter';
import UserCard from '../../components/UserCard/UserCard';
import selectUser from '../../store/selectors/appSelector';
import { selectUserListData } from '../../store/selectors/userListSelector';
import { PRIMARY_PURPLE } from '../../theme/colorConstants';
import { PRIMARY_FONT } from '../../theme/fontConstants';
import theme from '../../theme/theme';
import { getInitialSelectedShiftOptions, getNewShiftFilters } from '../../utils/jobOrderHelpers';

import DistanceFilterModal from './components/DistanceFilterModal/DistanceFilterModal';
import AddUserMenuButton from './AddUserMenuButton';
import {
  commitSsnLast4SearchValue,
  INDUSTRY_FILTER_OPTIONS,
  INITIAL_FILTER_SORT_PAGE,
  normalizeSsnLast4Input,
  ROLE_OPTIONS,
  SEARCH_PARAM_KEYS,
  SHIFT_OPTIONS_PAYLOAD,
  SHIFT_TIME_FILTER_OPTIONS,
  SORT_VALUES,
  STATUS_OPTIONS,
} from './UsersConstants';
import {
  agencyAPICallback,
  buildFilterPayloadFromUrl,
  buildSortPayloadFromUrl,
  capitalizeFirstLetter,
  getDistanceChipLabel,
  getMainHeaderKey,
  getSubHeaderKey,
  parseDistanceFilterFromUrl,
  recruiterAPICallback,
  writeDistanceFilterToSearchParams,
} from './UsersHelpers';
import { getUsers } from './usersListReducer';

const parseCsvSearchParam = (paramValue) =>
  paramValue
    ? paramValue
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)
    : [];

const loginSortOptions = [
  { value: SORT_VALUES.ASC, name: 'Oldest' },
  { value: SORT_VALUES.DESC, name: 'Newest' },
];

const checkboxSx = {
  color: PRIMARY_PURPLE[70],
  '&.Mui-checked': {
    color: PRIMARY_PURPLE[70],
  },
};

const checkboxLabelSx = {
  '& .MuiFormControlLabel-label': {
    color: PRIMARY_PURPLE[70],
  },
};

const payRateInputStyle = {
  '& .MuiOutlinedInput-notchedOutline': {
    border: '0px',
  },
  '& .MuiInputBase-input::placeholder': {
    color: theme?.button?.palette?.primary,
    fontFamily: PRIMARY_FONT['400'],
    opacity: 1,
  },
  border: `1px solid ${theme?.searchBoxForFilter?.borderColor?.focus}`,
  borderRadius: '5px',
  justifyContent: 'center',
  height: '28px',
  width: '110px',
};

const distanceChipSx = {
  height: '28px',
  borderRadius: '5px',
  fontFamily: 'Roboto-400',
  fontSize: '14px',
  color: PRIMARY_PURPLE[70],
  borderColor: PRIMARY_PURPLE[70],
  cursor: 'pointer',
  '& .MuiChip-icon': { fontSize: '18px', color: PRIMARY_PURPLE[70] },
  '& .MuiChip-deleteIcon': { color: PRIMARY_PURPLE[70] },
  '& .MuiChip-label': { color: PRIMARY_PURPLE[70] },
};

const UsersList = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const sentinelRef = useRef(null);
  const usersListData = useSelector(selectUserListData);
  const { isUsersLoading, totalRowCount, users } = usersListData;

  const [isResettingFilters, setIsResettingFilters] = useState(false);
  const [isDistanceModalOpen, setIsDistanceModalOpen] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsRef = useRef(searchParams);
  const distanceFilter = useMemo(() => parseDistanceFilterFromUrl(searchParams), [searchParams]);

  useEffect(() => {
    searchParamsRef.current = searchParams;
  }, [searchParams]);

  const [filterAndSortData, setFilterAndSortData] = useState(() => {
    const parsedUrlFilters = buildFilterPayloadFromUrl(searchParams, []);
    const parsedUrlSorts = buildSortPayloadFromUrl(searchParams, []);

    return {
      ...INITIAL_FILTER_SORT_PAGE,
      filters: [...INITIAL_FILTER_SORT_PAGE.filters, ...parsedUrlFilters],
      sortBy: [...parsedUrlSorts],
    };
  });

  const previousFiltersRef = useRef();

  const initialRoleValue =
    ROLE_OPTIONS.find((fillRate) => searchParams.get(SEARCH_PARAM_KEYS.ROLE) === fillRate.value) ||
    null;

  const initialStatus =
    STATUS_OPTIONS.find((status) => searchParams.get(SEARCH_PARAM_KEYS.STATUS) === status.value) ||
    null;

  const initialStatusV2 =
    STATUS_OPTIONS.find(
      (status) => searchParams.get(SEARCH_PARAM_KEYS.STATUS_V2) === status.value
    ) || null;

  const initialLoginSort =
    loginSortOptions.find(
      (loginSort) => searchParams.get(SEARCH_PARAM_KEYS.LOGIN_SORT) === loginSort.value
    ) || null;

  const isUserAdmin = user?.role === UserRole.ADMIN;
  const isUserRecruiter = user?.role === UserRole.RECRUITER;

  // For admins, Get agency filter from URL params
  const selectedAgencyId = searchParams.get(SEARCH_PARAM_KEYS.AGENCY) || null;
  // For admins and recruiters, Get recruiter filter from URL params
  const selectedRecruiterId = searchParams.get(SEARCH_PARAM_KEYS.RECRUITER) || null;
  // Handle recruiter agency filter - add it to state if not already present
  useEffect(() => {
    if (isUsersLoading) return;
    if (isUserRecruiter && user?.recruiter?.agency?.ID) {
      const hasAgencyFilter = filterAndSortData.filters.some(
        (filter) => filter.field === 'candidate.agency.ID' && filter.operation === 'equalsID'
      );

      if (!hasAgencyFilter) {
        setFilterAndSortData((prev) => ({
          ...prev,
          filters: [
            ...prev.filters,
            {
              field: 'candidate.agency.ID',
              operation: 'equalsID',
              value: user?.recruiter?.agency?.ID,
            },
          ],
        }));
      }
    }
  }, [isUserRecruiter, user?.recruiter?.agency?.ID, filterAndSortData.filters, isUsersLoading]);

  useEffect(() => {
    if (isUsersLoading) return;

    const payload = {
      filters: filterAndSortData.filters,
      limit: filterAndSortData.pageSize,
      page: filterAndSortData.page,
      sortBy: filterAndSortData.sortBy,
    };

    if (!isEqual(previousFiltersRef.current, payload)) {
      previousFiltersRef.current = payload;

      dispatch(getUsers(payload));
    }
  }, [dispatch, filterAndSortData, isUsersLoading, searchParams]);

  useEffect(() => {
    setFilterAndSortData((prev) => {
      const filters = buildFilterPayloadFromUrl(searchParams, prev.filters);
      const sorts = buildSortPayloadFromUrl(searchParams, prev.sortBy);

      if (isEqual(prev.filters, filters) && isEqual(prev.sortBy, sorts)) {
        return prev;
      }

      if (filters.length > 0 || sorts.length > 0) {
        return { ...prev, page: 0, filters, sortBy: sorts };
      }
      return { ...INITIAL_FILTER_SORT_PAGE, page: 0 };
    });
  }, [searchParams]);

  useEffect(() => {
    if (isResettingFilters) {
      setIsResettingFilters(false);
      setFilterAndSortData(INITIAL_FILTER_SORT_PAGE);
      setSearchParams({});
    }
  }, [isResettingFilters, setSearchParams]);

  const handleDistanceApply = useCallback(
    (values) => {
      setSearchParams(writeDistanceFilterToSearchParams(Object.fromEntries(searchParams), values));
    },
    [searchParams, setSearchParams]
  );

  const handleDistanceClear = useCallback(() => {
    setSearchParams(writeDistanceFilterToSearchParams(Object.fromEntries(searchParams), null));
  }, [searchParams, setSearchParams]);

  const distanceChipLabel = useMemo(() => getDistanceChipLabel(distanceFilter), [distanceFilter]);

  const handleIntersection = useCallback(
    (entries) => {
      if (entries?.[0].isIntersecting && totalRowCount > users?.length && !isUsersLoading) {
        setFilterAndSortData((prevVal) => ({
          ...prevVal,
          page: prevVal.page + 1,
        }));
      }
    },
    [users, totalRowCount, isUsersLoading]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: '0px',
      threshold: 1.0,
    });

    const currentSentinel = sentinelRef.current;

    if (currentSentinel) {
      observer.observe(currentSentinel);
    }

    return () => {
      if (currentSentinel) {
        observer.unobserve(currentSentinel);
      }
    };
  }, [handleIntersection]);

  const handleRoleChange = useCallback(
    (fillRate) => {
      const newParams = { ...Object.fromEntries(searchParams) };
      if (!fillRate) {
        delete newParams[SEARCH_PARAM_KEYS.ROLE];
      } else {
        newParams[SEARCH_PARAM_KEYS.ROLE] = fillRate;
      }

      if (newParams[SEARCH_PARAM_KEYS.ROLE] !== searchParams.get(SEARCH_PARAM_KEYS.ROLE)) {
        setSearchParams(newParams);
      }
    },
    [searchParams, setSearchParams]
  );

  const handleStatusChange = useCallback(
    (newStatus) => {
      const newParams = { ...Object.fromEntries(searchParams) };
      if (!newStatus) {
        delete newParams[SEARCH_PARAM_KEYS.STATUS];
      } else {
        newParams[SEARCH_PARAM_KEYS.STATUS] = newStatus;
      }

      if (newParams[SEARCH_PARAM_KEYS.STATUS] !== searchParams.get(SEARCH_PARAM_KEYS.STATUS)) {
        setSearchParams(newParams);
      }
    },
    [searchParams, setSearchParams]
  );

  const handleStatusV2Change = useCallback(
    (newStatus) => {
      const newParams = { ...Object.fromEntries(searchParams) };
      if (!newStatus) {
        delete newParams[SEARCH_PARAM_KEYS.STATUS_V2];
      } else {
        newParams[SEARCH_PARAM_KEYS.STATUS_V2] = newStatus;
      }

      if (
        newParams[SEARCH_PARAM_KEYS.STATUS_V2] !== searchParams.get(SEARCH_PARAM_KEYS.STATUS_V2)
      ) {
        setSearchParams(newParams);
      }
    },
    [searchParams, setSearchParams]
  );

  const handleLoginSortChange = useCallback(
    (newStatus) => {
      const newParams = { ...Object.fromEntries(searchParams) };
      if (!newStatus) {
        delete newParams[SEARCH_PARAM_KEYS.LOGIN_SORT];
      } else {
        newParams[SEARCH_PARAM_KEYS.LOGIN_SORT] = newStatus;
      }

      if (
        newParams[SEARCH_PARAM_KEYS.LOGIN_SORT] !== searchParams.get(SEARCH_PARAM_KEYS.LOGIN_SORT)
      ) {
        setSearchParams(newParams);
      }
    },
    [searchParams, setSearchParams]
  );

  const handleTextSearchChange = useCallback(
    (paramKey, newFilter) => {
      const searchTerm = newFilter?.[0]?.value || '';
      const currentParams = searchParamsRef.current;
      if ((currentParams.get(paramKey) || '') === searchTerm) return;

      const newParams = { ...Object.fromEntries(currentParams) };
      if (!searchTerm) {
        delete newParams[paramKey];
      } else {
        newParams[paramKey] = searchTerm;
      }

      // Keep the ref current so a second text filter in the same turn does not
      // overwrite this param with a stale query snapshot.
      searchParamsRef.current = new URLSearchParams(newParams);
      setSearchParams(newParams);
    },
    [setSearchParams]
  );

  const handleNameSearchChange = useCallback(
    (newNameFilter) => handleTextSearchChange(SEARCH_PARAM_KEYS.NAME, newNameFilter),
    [handleTextSearchChange]
  );

  const handleEmailSearchChange = useCallback(
    (newEmailFilter) => handleTextSearchChange(SEARCH_PARAM_KEYS.EMAIL, newEmailFilter),
    [handleTextSearchChange]
  );

  const handlePhoneSearchChange = useCallback(
    (newPhoneFilter) => handleTextSearchChange(SEARCH_PARAM_KEYS.PHONE, newPhoneFilter),
    [handleTextSearchChange]
  );

  const handleSsnLast4SearchChange = useCallback(
    (newSsnLast4Filter) => handleTextSearchChange(SEARCH_PARAM_KEYS.SSN_LAST4, newSsnLast4Filter),
    [handleTextSearchChange]
  );

  const handleAgencyChange = useCallback(
    (agencyId) => {
      const newParams = { ...Object.fromEntries(searchParams) };
      if (!agencyId || (Array.isArray(agencyId) && agencyId.length === 0)) {
        delete newParams[SEARCH_PARAM_KEYS.AGENCY];
      } else {
        newParams[SEARCH_PARAM_KEYS.AGENCY] = agencyId;
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const handleRecruiterChange = useCallback(
    (recruiterId) => {
      const newParams = { ...Object.fromEntries(searchParams) };
      if (!recruiterId || (Array.isArray(recruiterId) && recruiterId.length === 0)) {
        delete newParams[SEARCH_PARAM_KEYS.RECRUITER];
      } else {
        newParams[SEARCH_PARAM_KEYS.RECRUITER] = recruiterId;
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const handleTrueFlagFilterChange = useCallback(
    (paramKey, checked) => {
      const newParams = { ...Object.fromEntries(searchParams) };
      if (!checked) {
        delete newParams[paramKey];
      } else {
        newParams[paramKey] = 'true';
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const handleCheckboxFilterChange = useCallback(
    (paramKey, checked) => {
      const newParams = { ...Object.fromEntries(searchParams) };
      if (!checked) {
        delete newParams[paramKey];
      } else {
        newParams[paramKey] = '0';
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const handleMultiSelectFilterChange = useCallback(
    (paramKey, selectedValues) => {
      const newParams = { ...Object.fromEntries(searchParams) };
      if (!selectedValues || selectedValues.length === 0) {
        delete newParams[paramKey];
      } else {
        newParams[paramKey] = selectedValues.join(',');
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const handlePayRateChange = useCallback(
    (payRate) => {
      const newParams = { ...Object.fromEntries(searchParams) };
      if (payRate === null || payRate === undefined || payRate === '') {
        delete newParams[SEARCH_PARAM_KEYS.PAY_RATE_MEETS_PREF];
      } else {
        newParams[SEARCH_PARAM_KEYS.PAY_RATE_MEETS_PREF] = String(payRate);
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const [shiftSelectResetKey, setShiftSelectResetKey] = useState(0);

  const handleShiftOptionChange = useCallback(
    (selectedShifts) => {
      const newParams = { ...Object.fromEntries(searchParams) };
      delete newParams[SEARCH_PARAM_KEYS.SHIFT_START];
      delete newParams[SEARCH_PARAM_KEYS.SHIFT_END];

      if (!Array.isArray(selectedShifts) || selectedShifts.length === 0) {
        setSearchParams(newParams);
        return;
      }

      const selectedShiftFilters = selectedShifts
        .map((shift) => SHIFT_OPTIONS_PAYLOAD[shift])
        .filter(Boolean);
      const parsedShiftTime = getNewShiftFilters(selectedShiftFilters);
      const shiftStart = parsedShiftTime?.[0]?.value?.start;
      const shiftEnd = parsedShiftTime?.[0]?.value?.end;

      if (shiftStart !== undefined && shiftEnd !== undefined) {
        newParams[SEARCH_PARAM_KEYS.SHIFT_START] = String(shiftStart);
        newParams[SEARCH_PARAM_KEYS.SHIFT_END] = String(shiftEnd);
        setSearchParams(newParams);
        return;
      }

      // No usable betweenClockTimes range (e.g. all three shifts). Clear URL + remount UI.
      setSearchParams(newParams);
      setShiftSelectResetKey((key) => key + 1);
    },
    [searchParams, setSearchParams]
  );

  const initialSelectedIndustries = useMemo(
    () =>
      parseCsvSearchParam(searchParams.get(SEARCH_PARAM_KEYS.INDUSTRY_MEETS_PREF)).map(
        capitalizeFirstLetter
      ),
    [searchParams]
  );

  const initialPayRate = useMemo(
    () => searchParams.get(SEARCH_PARAM_KEYS.PAY_RATE_MEETS_PREF) || '',
    [searchParams]
  );

  const initialSelectedShiftTimes = useMemo(
    () =>
      getInitialSelectedShiftOptions(
        searchParams.get(SEARCH_PARAM_KEYS.SHIFT_START),
        searchParams.get(SEARCH_PARAM_KEYS.SHIFT_END)
      ),
    [searchParams]
  );

  const headerMsg = LanguageConverterV2(getMainHeaderKey(searchParams, user.role));
  const subHeaderMsg = LanguageConverterV2(getSubHeaderKey(searchParams));

  const MainListContent = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <Box>
        <Typography sx={{ fontFamily: 'Barlow', fontSize: '32px', fontWeight: 800 }}>
          {headerMsg}
        </Typography>
        <Typography sx={{ fontFamily: 'Barlow', fontSize: '20px', fontWeight: 400 }}>
          {subHeaderMsg} ({totalRowCount})
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: '25px',
        }}
      >
        {users?.map((u) => (
          <UserCard user={u} key={u?.id} userRole={user?.role} />
        ))}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', padding: '24px', gap: '24px' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <AddUserMenuButton />
        {!isResettingFilters && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>
              {LanguageConverterV2('buttonText.filters')}
            </Typography>

            {isUserAdmin && (
              <SelectDropdownFilter
                disabled={isUsersLoading}
                placeholder={LanguageConverterV2('userList.filter.role.placeholder')}
                onValueChange={handleRoleChange}
                options={ROLE_OPTIONS}
                getOptionLabel={(option) => option.name}
                wrapperSx={{ width: '180px', padding: '0px 6px' }}
                initialValue={initialRoleValue}
              />
            )}

            <SelectDropdownFilter
              disabled={isUsersLoading}
              placeholder={LanguageConverterV2(
                isUserAdmin
                  ? 'userList.filter.statusV1.placeholder'
                  : 'userList.filter.status.placeholder'
              )}
              onValueChange={handleStatusChange}
              options={STATUS_OPTIONS}
              getOptionLabel={(option) => option.name}
              startAdornmentIcon={
                <Box
                  component="img"
                  sx={{
                    height: 16,
                    width: 16,
                  }}
                  alt="Status"
                  src={StatusIcon}
                />
              }
              initialValue={initialStatus}
            />

            {isUserAdmin && (
              <SelectDropdownFilter
                disabled={isUsersLoading}
                placeholder={LanguageConverterV2('userList.filter.statusV2.placeholder')}
                onValueChange={handleStatusV2Change}
                options={STATUS_OPTIONS}
                getOptionLabel={(option) => option.name}
                startAdornmentIcon={
                  <Box
                    component="img"
                    sx={{
                      height: 16,
                      width: 16,
                    }}
                    alt="Status V2"
                    src={StatusIcon}
                  />
                }
                initialValue={initialStatusV2}
              />
            )}

            {isUserAdmin && (
              <SearchableSelectDropdownFilterDeprecated
                disabled={isUsersLoading}
                placeholder={LanguageConverterV2('userList.filter.agency.placeholder')}
                onValueChange={handleAgencyChange}
                optionsAPICallback={agencyAPICallback(selectedAgencyId)}
                getOptionLabel={(option) => option.name}
                initialValue={selectedAgencyId}
                wrapperSx={{ width: '200px', padding: '0px 6px' }}
              />
            )}

            {(isUserAdmin || isUserRecruiter) && (
              <SearchableSelectDropdownFilterDeprecated
                disabled={isUsersLoading}
                placeholder={LanguageConverterV2('userList.filter.recruiter.placeholder')}
                onValueChange={handleRecruiterChange}
                optionsAPICallback={recruiterAPICallback(selectedRecruiterId)}
                getOptionLabel={(option) => option.name}
                initialValue={selectedRecruiterId}
                wrapperSx={{ width: '200px', padding: '0px 6px' }}
              />
            )}
            <FreeTextSearchFilter
              placeholder={LanguageConverterV2('userList.filter.name.placeholder')}
              onValueChange={handleNameSearchChange}
              field="name"
              operation="icontains"
              background={SEARCHBAR_BACKGROUND.DEFAULT}
              initialValue={{ value: searchParams.get(SEARCH_PARAM_KEYS.NAME) }}
            />
            <FreeTextSearchFilter
              placeholder={LanguageConverterV2('userList.filter.email.placeholder')}
              onValueChange={handleEmailSearchChange}
              field="email"
              operation="icontains"
              background={SEARCHBAR_BACKGROUND.DEFAULT}
              initialValue={{ value: searchParams.get(SEARCH_PARAM_KEYS.EMAIL) }}
            />
            <FreeTextSearchFilter
              placeholder={LanguageConverterV2('userList.filter.phone.placeholder')}
              onValueChange={handlePhoneSearchChange}
              field="phone.phoneNumber"
              operation="icontains"
              background={SEARCHBAR_BACKGROUND.DEFAULT}
              initialValue={{ value: searchParams.get(SEARCH_PARAM_KEYS.PHONE) }}
            />
            <FreeTextSearchFilter
              placeholder={LanguageConverterV2('userList.filter.ssnLast4.placeholder')}
              onValueChange={handleSsnLast4SearchChange}
              field="ssnLast4"
              operation="equals"
              background={SEARCHBAR_BACKGROUND.DEFAULT}
              initialValue={{ value: searchParams.get(SEARCH_PARAM_KEYS.SSN_LAST4) }}
              normalizeValue={normalizeSsnLast4Input}
              commitValue={commitSsnLast4SearchValue}
            />

            {distanceFilter ? (
              <Chip
                icon={<LocationIcon sx={{ color: PRIMARY_PURPLE[70] }} />}
                label={distanceChipLabel}
                onClick={() => setIsDistanceModalOpen(true)}
                onDelete={handleDistanceClear}
                deleteIcon={<ClearIcon sx={{ width: 14, height: 14 }} />}
                sx={{
                  ...distanceChipSx,
                  maxWidth: '280px',
                  backgroundColor: PRIMARY_PURPLE[25],
                  border: `1px solid ${PRIMARY_PURPLE[70]}`,
                  '& .MuiChip-label': {
                    color: PRIMARY_PURPLE[70],
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
              />
            ) : (
              <Chip
                icon={<LocationIcon sx={{ color: PRIMARY_PURPLE[70] }} />}
                label="Distance"
                onClick={() => setIsDistanceModalOpen(true)}
                variant="outlined"
                sx={distanceChipSx}
              />
            )}

            <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>
              {LanguageConverterV2('user.lastLoggedIn')}
            </Typography>

            <SelectDropdownFilter
              disabled={isUsersLoading}
              placeholder={LanguageConverterV2('buttonText.unsorted')}
              onValueChange={handleLoginSortChange}
              options={loginSortOptions}
              getOptionLabel={(option) => option.name}
              initialValue={initialLoginSort}
            />

            <MultipleSelectDropdownChip
              disabled={isUsersLoading}
              options={INDUSTRY_FILTER_OPTIONS}
              placeholder={LanguageConverterV2('userList.filter.industryMeetsPref.label')}
              allOptionsLabel={LanguageConverterV2('userList.filter.industryMeetsPref.label')}
              onValueChange={(selected) =>
                handleMultiSelectFilterChange(
                  SEARCH_PARAM_KEYS.INDUSTRY_MEETS_PREF,
                  selected.map((value) => value.toLowerCase())
                )
              }
              defaultSelectedOptions={initialSelectedIndustries}
            />

            <TextField
              disabled={isUsersLoading}
              placeholder={LanguageConverterV2('userList.filter.payRateMeetsPref.label')}
              type="number"
              value={initialPayRate}
              onChange={(e) => {
                const { value } = e.target;
                const parsed = parseInt(value, 10);
                handlePayRateChange(value === '' || Number.isNaN(parsed) ? null : parsed);
              }}
              onKeyDown={(e) => {
                if (e.key === '-' || e.key === 'e') e.preventDefault();
              }}
              inputProps={{ min: 0 }}
              size="small"
              sx={payRateInputStyle}
            />

            <MultipleSelectDropdownChip
              key={shiftSelectResetKey}
              disabled={isUsersLoading}
              options={SHIFT_TIME_FILTER_OPTIONS}
              placeholder={LanguageConverterV2('userList.filter.shiftTimeMeetsPref.label')}
              allOptionsLabel={LanguageConverterV2('userList.filter.shiftTimeMeetsPref.label')}
              onValueChange={handleShiftOptionChange}
              defaultSelectedOptions={initialSelectedShiftTimes}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={searchParams.has(SEARCH_PARAM_KEYS.NO_FUTURE_WORK)}
                  onChange={(e) =>
                    handleCheckboxFilterChange(SEARCH_PARAM_KEYS.NO_FUTURE_WORK, e.target.checked)
                  }
                  disabled={isUsersLoading}
                  sx={checkboxSx}
                />
              }
              label={LanguageConverterV2('userList.filter.noFutureWork.label')}
              sx={checkboxLabelSx}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={searchParams.get(SEARCH_PARAM_KEYS.WOTC_ELIGIBLE) === 'true'}
                  onChange={(e) =>
                    handleTrueFlagFilterChange(SEARCH_PARAM_KEYS.WOTC_ELIGIBLE, e.target.checked)
                  }
                  disabled={isUsersLoading}
                  sx={checkboxSx}
                />
              }
              label={LanguageConverterV2('userDetail.taxCredits.wotcEligible')}
              sx={checkboxLabelSx}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={searchParams.get(SEARCH_PARAM_KEYS.URBAN_YOUTH_CERTIFIED) === 'true'}
                  onChange={(e) =>
                    handleTrueFlagFilterChange(
                      SEARCH_PARAM_KEYS.URBAN_YOUTH_CERTIFIED,
                      e.target.checked
                    )
                  }
                  disabled={isUsersLoading}
                  sx={checkboxSx}
                />
              }
              label={LanguageConverterV2('userDetail.taxCredits.urbanYouthCertified')}
              sx={checkboxLabelSx}
            />
          </Box>
        )}
      </Box>
      {users?.length === 0 && !isUsersLoading ? (
        <NoResultsCard
          headerMsgKey={null}
          bodyMsgKey="jobOrder.search.noResult"
          onResetFilter={() => {
            setIsResettingFilters(true);
          }}
        />
      ) : (
        <>
          <MainListContent />
          {isUsersLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          )}
        </>
      )}

      <div ref={sentinelRef} />

      <DistanceFilterModal
        open={isDistanceModalOpen}
        onClose={() => setIsDistanceModalOpen(false)}
        onApply={handleDistanceApply}
        initialValues={distanceFilter}
      />
    </Box>
  );
};

export default UsersList;
